import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, type User } from '../services/auth.service';

export class TwoFactorRequiredError extends Error {
  email: string;
  constructor(email: string) {
    super('Two-factor authentication required');
    this.name = 'TwoFactorRequiredError';
    this.email = email;
  }
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  verifyTwoFactor: (email: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAdmin: () => boolean;
  isManagerOrAbove: () => boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
      authService.clearTokens();
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Try to refresh session using HttpOnly cookie
      // This handles page refresh where in-memory token is lost
      try {
        await refreshUser();
      } catch {
        // No valid session, user needs to login
      }
      setIsLoading(false);
    };

    initAuth();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);

    // If 2FA is required, throw a special error so LoginPage can handle it
    if (response.requiresTwoFactor) {
      throw new TwoFactorRequiredError(email);
    }

    setUser(response.user);
  };

  const verifyTwoFactor = async (email: string, token: string) => {
    const response = await authService.verifyTwoFactor(email, token);
    setUser(response.user);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      authService.clearTokens();
    }
  };

  const isAdmin = useCallback((): boolean => {
    return user?.role === 'ADMIN';
  }, [user]);

  const isManagerOrAbove = useCallback((): boolean => {
    return user?.role === 'ADMIN' || user?.role === 'MANAGER';
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        verifyTwoFactor,
        logout,
        refreshUser,
        isAdmin,
        isManagerOrAbove,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
